import { useEffect } from 'react';
import * as Notifications from 'expo-notifications';
import { router } from 'expo-router';

import { useHasLoggedOut } from '../../context/AuthMachine';
import {
  ORDER_FEEDBACK_NOTIFICATION_ANDROID_IMPORTANCE,
  ORDER_FEEDBACK_NOTIFICATION_ANDROID_VISIBILITY_PUBLIC,
  ORDER_FEEDBACK_NOTIFICATIONS_CHANNEL_ID,
} from './useOrderFeedbackNotifications.constants';
import {
  cancelAllOrderFeedbackNotifications,
  checkIfOrderFeedbackNotification,
  checkIfOrderFeedbackNotificationsEnabled,
} from './useOrderFeedbackNotifications.utils';

// ─────────────────────────────────────────────────────────────────────────────

// Creates push notifications channel on Android.
createOrderStatusChannel();

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Handles incoming order feedback notifications: sets up the Android channel,
 * routes notification taps to the rate-order screen (foreground/background +
 * cold start), and cancels pending notifications on logout.
 *
 * NOTE: Native platforms only
 */
export const useHandleOrderFeedbackNotifications = () => {
  const hasCustomerLoggedOut = useHasLoggedOut();

  // ─── Effects ─────────────────────────────────────────────────────────

  // foreground/background tap listener
  useEffect(() => {
    const subscription = Notifications.addNotificationResponseReceivedListener((response) => {
      handleOrderFeedbackNotificationResponse(response);
    });

    return () => {
      subscription.remove();
    };
  }, []);

  // cold-start tap (one-shot per app session)
  useEffect(() => {
    const response = Notifications.getLastNotificationResponse();

    if (!response) return;

    handleOrderFeedbackNotificationResponse(response);
  }, []);

  // cancel all scheduled feedback notifications on logout
  useEffect(() => {
    if (!checkIfOrderFeedbackNotificationsEnabled() || !hasCustomerLoggedOut) return;

    void cancelAllOrderFeedbackNotifications();
  }, [hasCustomerLoggedOut]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function createOrderStatusChannel() {
  if (!checkIfOrderFeedbackNotificationsEnabled()) return;

  void Notifications.setNotificationChannelAsync(ORDER_FEEDBACK_NOTIFICATIONS_CHANNEL_ID, {
    name: 'Order feedback notifications',
    importance: ORDER_FEEDBACK_NOTIFICATION_ANDROID_IMPORTANCE,
    lockscreenVisibility: ORDER_FEEDBACK_NOTIFICATION_ANDROID_VISIBILITY_PUBLIC,
  });
}

function handleOrderFeedbackNotificationResponse(response: Notifications.NotificationResponse) {
  const { data } = response.notification.request.content;

  const isValidNotification = checkIfOrderFeedbackNotification(response.notification.request);

  if (!isValidNotification) return;

  const orderId = typeof data?.orderId === 'string' ? data.orderId : undefined;

  if (!orderId) return;

  router.dismissAll();
  router.navigate(`/feedback/${orderId}` as never);
}
